"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import ArrowIosLeft from "@crehana/compass.icons.outline/ArrowIosLeft";
import ArrowIosRight from "@crehana/compass.icons.outline/ArrowIosRight";
import { useTranslation } from "@crehana/i18n";
import { cva } from "class-variance-authority";
import { CommandItem } from "../_shadcn-native-components/command.js";
import { Checkbox } from "../Checkbox/index.js";
import { cn } from "../utils.js";
import { useDropdownContext } from "./context.js";
import { DropdownBaseItem } from "./DropdownBaseItem.js";
import { DropdownBaseList } from "./DropdownBaseList.js";
import i18n from "./i18n/index.js";
const ICON_VARIANTS = {
    default: 'tw-bg-neutral-light-300 dark:tw-bg-neutral-dark-600 tw-text-neutral-light-700 dark:tw-text-neutral-dark-300 group-aria-selected:tw-bg-neutral-light-200 dark:group-aria-selected:tw-bg-neutral-dark-500',
    'default-selected': 'tw-text-primary-light-500 dark:tw-text-secondary-dark-500 dark:tw-bg-neutral-dark-700 dark:group-aria-selected:tw-bg-neutral-dark-700',
    primary: 'tw-text-primary-light-500 tw-bg-primary-light-100',
    'primary-selected': 'tw-text-primary-light-500 tw-bg-primary-light-100',
    success: 'tw-text-success-500 tw-bg-success-100 tw-bg-success-100',
    'success-selected': 'tw-text-success-500 tw-bg-success-100',
    alert: 'tw-text-alert-500 tw-bg-alert-100 tw-bg-alert-100',
    'alert-selected': 'tw-text-alert-500 tw-bg-alert-100',
    info: 'tw-text-info-500 tw-bg-info-100',
    'info-selected': 'tw-text-info-500 tw-bg-info-100',
    disabled: 'tw-text-neutral-light-300 dark:tw-text-neutral-dark-300'
};
const iconVariants = cva('tw-min-h-4 tw-min-w-4 tw-rounded-1 tw-flex tw-items-center tw-justify-center', {
    variants: {
        variant: ICON_VARIANTS
    },
    defaultVariants: {
        variant: 'default'
    }
});
const getParsedVariant = ({ iconColorVariant = 'default', isSelected })=>isSelected ? `${iconColorVariant}-selected` : iconColorVariant;
const DropdownLeveledList = ({ className, items, selected, onSelect, isCheckbox = true })=>{
    const { languageCode } = useTranslation();
    const { values, setItems, setValues } = useDropdownContext();
    const [selectedOption, setSelectedOption] = useState();
    const t = i18n[languageCode];
    useEffect(()=>{
        setItems(items.map((item)=>({
                ...item,
                type: 'option'
            })));
    }, [
        items
    ]);
    const selection = selected ?? values;
    const updateValues = useCallback((newValues)=>{
        setValues(newValues);
        if (onSelect) onSelect(newValues);
    }, []);
    const handleSelection = (value, state)=>{
        const newSelection = isCheckbox ? state ? [
            ...selection,
            value
        ] : selection?.filter((item)=>item !== value) : state ? [
            value
        ] : [];
        if (!isCheckbox && selectedOption) newSelection.push(selectedOption);
        updateValues(newSelection);
    };
    const handleClearCategory = ()=>{
        const subcategories = items.find((item)=>item.value === selectedOption)?.items ?? [];
        if (subcategories.length > 0) {
            const newSelection = selection?.filter((item)=>!subcategories.map((subcategory)=>subcategory.value).includes(item));
            updateValues(newSelection);
        }
    };
    return /*#__PURE__*/ jsx(DropdownBaseList, {
        selected: selected,
        className: className,
        children: selectedOption ? /*#__PURE__*/ jsxs("div", {
            className: "tw-relative tw-flex tw-flex-col",
            children: [
                /*#__PURE__*/ jsxs("button", {
                    onClick: ()=>setSelectedOption(void 0),
                    className: cn('tw-flex tw-items-center tw-gap-0.4 tw-sticky tw-top-0 tw-z-2', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'tw-w-full tw-text-left tw-py-0.8 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
                    children: [
                        /*#__PURE__*/ jsx(ArrowIosLeft, {
                            size: 2,
                            className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
                        }),
                        t.DROPDOWN_OPTIONS_BACK_BUTTON
                    ]
                }),
                isCheckbox ? /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        items.find((item)=>item.value === selectedOption)?.items.map((item)=>{
                            const isSelected = selection?.includes(item.value);
                            return /*#__PURE__*/ jsxs(CommandItem, {
                                className: "tw-justify-start",
                                value: item.value.toString(),
                                removeCheck: true,
                                onSelect: ()=>handleSelection(item.value, !isSelected),
                                children: [
                                    /*#__PURE__*/ jsx(Checkbox, {
                                        checked: isSelected
                                    }),
                                    item.label
                                ]
                            }, item.value);
                        }),
                        /*#__PURE__*/ jsx("button", {
                            onClick: handleClearCategory,
                            className: cn('tw-w-full tw-sticky tw-bottom-0 tw-text-left tw-py-1.2 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
                            children: t.DROPDOWN_OPTIONS_ERASE_ALL
                        })
                    ]
                }) : items.find((item)=>item.value === selectedOption)?.items.map((item)=>{
                    const isSelected = values.includes(item.value);
                    return /*#__PURE__*/ jsx(DropdownBaseItem, {
                        value: item.value,
                        searchKey: item.label,
                        isSelected: isSelected,
                        onSelect: ()=>handleSelection(item.value, !isSelected),
                        className: cn(isSelected && 'tw-bg-neutral-light-300 dark:tw-bg-neutral-dark-600', className),
                        children: item.label
                    }, item.value);
                })
            ]
        }) : items.map((item)=>{
            const categoryItems = items.find((cat)=>cat.value === item.value)?.items ?? [];
            const selectedCount = selection?.filter((select)=>categoryItems.find((item)=>item.value === select)).length;
            return /*#__PURE__*/ jsxs(CommandItem, {
                value: item.value.toString(),
                removeCheck: true,
                onSelect: ()=>{
                    setSelectedOption(item.value);
                    const arrayFilter = items.find((it)=>it.value === item.value)?.items;
                    setItems(arrayFilter);
                },
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: "tw-flex tw-items-center tw-gap-0.8",
                        children: [
                            item.icon && /*#__PURE__*/ jsx("div", {
                                className: cn(iconVariants({
                                    variant: getParsedVariant({
                                        iconColorVariant: item.iconColorVariant,
                                        isSelected: false
                                    })
                                })),
                                children: item.icon({
                                    size: item.iconSize || 24,
                                    className: 'tw-text-inherit tw-flex-shrink-0'
                                })
                            }),
                            item.label,
                            selectedCount > 0 && /*#__PURE__*/ jsx("span", {
                                className: "tw-rounded tw-flex tw-items-center tw-justify-center tw-w-2 tw-h-2 tw-font-body5 tw-bg-primary-light-500 tw-text-neutral-light-100",
                                children: selectedCount
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(ArrowIosRight, {
                        size: 24,
                        className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
                    })
                ]
            }, item.value);
        })
    });
};
export { DropdownLeveledList };
